import 'swiper/swiper-bundle.css';
import { Swiper, SwiperSlide, SwiperClass } from 'swiper/react';
import { memo, useEffect, useRef, useState } from 'react';
import { Call } from '../types.ts';
import {
  landingYoutube,
  launchYoutube,
  popupToast,
} from '@/api/nativeBridge.ts';

const DiscoverSwiper = memo(
  ({
    callItem,
    mainIndex,
    dataIndex,
    isPack,
  }: {
    callItem: Call;
    mainIndex: number;
    dataIndex: number;
    isPack: boolean;
  }) => {
    const swiperRef = useRef<SwiperClass | null>(null); // Swiper 인스턴스를 저장할 Ref
    const [slideCount, setSlideCount] = useState(0);
    const [isCentered, setIsCentered] = useState<boolean>(false);

    const clickYoutube = (videoId: string, title: string, index: number) => {
      if (!videoId) {
        popupToast(1, '유튜브 쇼츠가 존재하지 않습니다.');
        return;
      }

      const pageIndex = (dataIndex + 2).toString();
      const itemIndex = (index + 1).toString();

      if (isPack) {
        const slideCount = swiperRef.current?.slides.length ?? 0;
        launchYoutube(
          videoId,
          title.substring(0, 100),
          `${pageIndex}_${itemIndex}`,
          slideCount,
        );
      } else {
        if (mainIndex === 0) {
          landingYoutube(videoId, itemIndex);
        } else {
          landingYoutube(videoId, itemIndex, (mainIndex + 1).toString());
        }
      }
    };

    // const onTouchEndYoutube = (e: React.TouchEvent, videoId: string, index: number) => {
    //     console.log('onTouchEnd Youtubue')
    //     e.preventDefault()
    //     clickYoutube(videoId, index)
    // }

    useEffect(() => {
      if (swiperRef.current) {
        const count = swiperRef.current.slides.length; // 슬라이드 개수 가져오기
        setSlideCount(count);

        const swiperWrappers = document.querySelectorAll(
          '.row-slide-section .swiper-wrapper',
        ) as NodeListOf<HTMLDivElement>;

        swiperWrappers.forEach((wrapper) => {
          const rowSlideSection = wrapper.closest(
            '.row-slide-section',
          ) as HTMLDivElement;
          const slideItems = wrapper.querySelectorAll('.swiper-slide'); // 각 wrapper 내부의 슬라이드 개수
          const count = slideItems.length; // 각 wrapper의 슬라이드 개수

          // 슬라이드 길이 계산
          const rowSlideItem = wrapper.querySelector(
            '.swiper-slide',
          ) as HTMLDivElement;
          const ItemMargin =
            Number(swiperRef.current?.params.spaceBetween) || 0;
          const shortsWidth = rowSlideItem ? rowSlideItem.offsetWidth : 0;
          const rowSlideLength = slideItems.length;
          const swiperWidth =
            shortsWidth * rowSlideLength +
            ItemMargin * (rowSlideLength - 1) +
            40; // 슬라이드 전체 길이

          if (count === 1) {
            // 슬라이드 1개일경우
            wrapper.style.justifyContent = 'center';
          } else if (count === 2) {
            // 슬라이드 2개일경우
            if (rowSlideSection && rowSlideSection.offsetWidth > 345) {
              // 345px 보다 큰경우
              wrapper.style.justifyContent = 'center';
            } else if (rowSlideSection && rowSlideSection.offsetWidth <= 345) {
              wrapper.style.justifyContent = 'flex-start';
            }
          } else if (count === 3) {
            // 슬라이드 3개일경우
            if (rowSlideSection && rowSlideSection.offsetWidth > 518) {
              // 슬라이드 전체 길이보다 브라우저가 큰경우
              wrapper.style.justifyContent = 'center';
            } else if (wrapper && wrapper.offsetWidth <= 518) {
              // 슬라이드 전체 길이보다 브라우저가 작은경우
              wrapper.style.justifyContent = 'flex-start';
            }
          } else if (count >= 4) {
            // 슬라이드 4개이상일경우
            if (rowSlideSection && rowSlideSection.offsetWidth > swiperWidth) {
              // 슬라이드 전체 길이보다 브라우저가 큰경우
              wrapper.style.justifyContent = 'center';
            } else if (wrapper && wrapper.offsetWidth <= swiperWidth) {
              // 슬라이드 전체 길이보다 브라우저가 작은경우
              wrapper.style.justifyContent = 'flex-start';
            }
          }
        });
      }
    }, []);

    // 왼쪽정렬유지..
    const handleSlideChange = (swiper: SwiperClass) => {
      const activeIndex = swiper.realIndex; // activeIndex 가져오기

      // 슬라이드 정렬
      if (activeIndex === 0 || swiper.isEnd) {
        setIsCentered(false); // 첫 번째 & 마지막 슬라이드는 왼쪽 정렬 유지
      } else {
        setIsCentered(true); // 중간 슬라이드는 중앙 정렬
      }

      // 3개일경우 강제 정렬
      if (slideCount === 3) {
        const activeIndex = swiper.activeIndex; // activeIndex 가져오기
        const targetSlide = swiper.slides[activeIndex] as HTMLElement; // 타입지정
        const slideItemWidth = targetSlide.offsetWidth; // slide item 가로값
        const targetPos = targetSlide.offsetLeft + slideItemWidth / 2; // 슬라이드 이동 위치
        const slideWidth = swiper.width; // slide 전체 가로값
        let translateValue = 0; // 슬라이드 위치값

        // 중앙 정렬 계산
        if (targetPos <= slideWidth / 2) {
          // 1번슬라이드
          translateValue = 0;
        } else {
          // 2번슬라이드
          translateValue = targetPos - slideWidth / 2; // 슬라이드 이동 위치 - slide 전체 가로값 / 2
        }

        // Swiper의 위치 설정 및 업데이트
        swiper.wrapperEl.style.transform = `translate3d(-${translateValue}px, 0, 0)`;
        swiper.wrapperEl.style.transitionDuration = '500ms';
        swiper.updateProgress();
      }
    };

    return (
      <div className="row-slide-section">
        <Swiper
          onSwiper={(swiper) => (swiperRef.current = swiper)} // Swiper 인스턴스를 Ref에 저장
          slidesPerView={'auto'}
          spaceBetween={slideCount === 2 ? 12 : 20} // 2개일때 12px간격
          observer={true}
          observeParents={true}
          nested={true} // 터치 이벤트 충돌을 방지
          threshold={15} // 스와이프 민감도 px
          centeredSlides={isCentered}
          // centeredSlides={slideCount === 3 ? false : isCentered}
          centeredSlidesBounds={true}
          onActiveIndexChange={handleSlideChange}
        >
          {callItem.searchResult?.map((search, index) => (
            <SwiperSlide key={`swiper-item-youtube-${index}`}>
              <div
                className={`card-item card-item${index}`}
                onClick={() =>
                  clickYoutube(
                    search.youtube.contents[0].videoId,
                    search.youtube.query,
                    index,
                  )
                }
                // onTouchEnd={(e: React.TouchEvent) => onTouchEndYoutube(e, search.youtube.contents[0].videoId, index)}
              >
                <div className="card-img-box">
                  {/* {index === 0 && isFocused ? (
                                    <YoutubeTransition
                                        videoId={search.youtube.contents[0].videoId}
                                        thumbnailUrl={search.youtube.contents[0].thumbnailUrl}
                                        isFocused={isFocused}
                                    />
                                ) : (
                                    <>
                                        <img src={search.youtube.contents[0].thumbnailUrl} alt='' />
                                        {search.youtube.contents[0].isShort ? (
                                            <span className='icon-shorts'>SHORTS</span>
                                        ) : (
                                            <></>
                                        )}
                                    </>
                                )} */}
                  <img
                    src={search.youtube.contents?.[0]?.thumbnailUrl
                      .replace('/vi/', '/vi_webp/')
                      .replace('.jpg', '.webp')}
                    alt={`${search.youtube.query}`}
                    loading="lazy"
                    onError={(e) => {
                      const target = e.currentTarget;
                      if (target.src.includes('/vi_webp/')) {
                        target.onerror = null; // 무한 루프 방지
                        target.src = search.youtube.contents?.[0]?.thumbnailUrl;
                      } else {
                        // 원본 URL에서도 에러가 발생하면 디폴트 이미지로 설정
                        target.onerror = null;
                        target.src = '/public/res/img/rotate_card_empty.webp';
                      }
                    }}
                  />

                  {search.youtube.contents[0].isShort ? (
                    <span className="icon-shorts">SHORTS</span>
                  ) : (
                    <></>
                  )}
                </div>
                <div className="card-text-box">
                  {search.youtube.tags.map((tag, index) => (
                    <span
                      className="item"
                      key={`${search.youtube.contents[0].thumbnailUrl}-tag-${index}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  },
);

export default DiscoverSwiper;
